<template>
  <div class="add-offer">
    <vue-content-loading v-if="offer != null && loadingForm" :height="1000">
      <rect x="5" y="100" rx="4" ry="4" width="97%" height="20" />
      <rect x="5" y="160" rx="4" ry="4" width="97%" height="20" />
      <rect x="5" y="240" rx="4" ry="4" width="97%" height="20" />
      <rect x="5" y="300" rx="4" ry="4" width="97%" height="50" />
      <rect x="5" y="380" rx="4" ry="4" width="97%" height="20" />
      <rect x="25%" y="440" rx="4" ry="4" width="50%" height="50" />
    </vue-content-loading>

    <div class="add-offer__body">
      <h1 class="add-offer__body__title">
        <span v-if="offer == null">Create a new offer or event</span>
        <span v-else>Edit offer or event</span>
      </h1>
      <v-divider />
      <v-card class="valid-offer">
        <p v-if="invalidOffer" class="pa-2 white--text">Invalid or missing values</p>
      </v-card>
      <v-row>
        <v-col cols="12" lg="6" class="mt-3">
          <p class="grey--text">Your business</p>
        </v-col>

        <v-col cols="12" lg="6">
          <v-autocomplete
            name="merchant"
            placeholder="E.g 'McDonald's'"
            :items="allMerchants"
            attach
            item-text="name"
            item-value="name"
            solo
            flat
            outlined
            :menu-props="{
              value: merchantSelectMenu,
              closeOnClick: true,
              closeOnContentClick: true,
              openOnClick: true
            }"
            return-object
            :search-input.sync="searchMerchant"
            v-model="offerTemplate.merchant"
            append-icon
            class="add-offer__body--autocomplete"
            :loading="loadingMerchant"
            :rules="rules.requiredRules"
          >
            <template v-slot:prepend-item>
              <v-list-item-content>
                <v-btn text @click="showMerchantModal()">Add new merchant</v-btn>
              </v-list-item-content>
              <v-divider />
            </template>

            <template v-slot:item="data">
              <v-avatar left style="background-color: #f1f1f1;">
                <v-img :src="createMerchantUrl(data.item)" v-if="data.item.logo" />
              </v-avatar>
              <v-list-item-content class="pl-2">
                {{
                data.item.name
                }}
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col lg="6" xs="12" class="mt-3">
          <p class="grey--text">Choose outlet locations</p>
        </v-col>

        <v-col v-if="offerTemplate.merchant" cols="12" lg="6">
          <v-autocomplete
            name="locations"
            placeholder="E.g. 'Armories Drive, Leeds LS10 1LT"
            :items="outlets"
            attach
            :menu-props="{
              value: merchantSelectMenu,
              closeOnClick: true,
              closeOnContentClick: true,
              openOnClick: true
            }"
            return-object
            multiple
            solo
            flat
            outlined
            rounded
            item-text="address"
            item-value="address"
            v-model="offerTemplate.applies"
            append-icon
            :disabled="!canAddOutlet"
            :rules="rules.requiredRules"
            class="add-offer__body--autocomplete"
          >
            <template v-slot:prepend-item>
              <v-list-item-content>
                <div class="px-5" style="display: flex; justify-content: space-between;">
                  <v-checkbox
                    v-if="offerTemplate.applies && offerTemplate.applies > 0"
                    v-model="selectAllOutlets"
                    label="Select all"
                  ></v-checkbox>
                  <v-btn text @click="showOutletModal()">Add new</v-btn>
                </div>
              </v-list-item-content>
              <v-divider />
            </template>

            <template v-slot:item="data">
              <v-list-item-content class="pl-2">
                {{
                data.item.address.address
                }}
              </v-list-item-content>
            </template>

            <template v-slot:selection="{ item, index }">
              <v-list-item-content class="pl-2">
                <v-chip v-if="index === 0">{{ item.address.address }}</v-chip>
                <span
                  v-if="index == 1"
                  class="grey--text caption"
                >(+{{ offerTemplate.applies.length - 1}} others)</span>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <h1 class="mb-3" style="font-weight: 300;">Offer details</h1>
      <v-divider />

      <!--Offer/event-->
      <div>
        <v-row>
          <v-col cols="12" lg="6" class="mt-3">
            <p class="grey--text">The title of your offer or event (max 65 characters)</p>
          </v-col>
          <v-col cols="12" lg="6">
            <v-text-field
              type="text"
              name="offer/event"
              placeholder="Offer/Event title"
              v-model="offerTemplate.title"
              required
              outlined
              solo
              flat
              rounded
              :rules="rules.titleRules"
            ></v-text-field>
          </v-col>
          <v-row>
            <v-col
              :class="{
                'valid-hint-md': this.$vuetify.breakpoint.md,
                'valid-hint-xs': this.$vuetify.breakpoint.xs
              }"
            >
              <v-subheader
                :class="{
                  'text-limit': offerTemplate.title.length > 75
                }"
              >{{ offerTemplate.title.length }}/75</v-subheader>
            </v-col>
          </v-row>
        </v-row>
      </div>

      <!--Teaser-->
      <v-row>
        <v-col cols="12" lg="6" class="mt-3">
          <p class="grey--text">A two line teaser to make your reader click (max 125 characters)</p>
        </v-col>
        <v-col cols="12" lg="6">
          <textarea
            name="two-line-teaser"
            placeholder="Two line teaser"
            v-model="offerTemplate.tagline"
            :rules="rules.taglineRules"
          />
          <v-row>
            <v-col
              :class="{
                'valid-hint-md': this.$vuetify.breakpoint.md,
                'valid-hint-xs': this.$vuetify.breakpoint.xs
              }"
            >
              <v-subheader
                :class="{ 'text-limit': offerTemplate.tagline.length > 125 }"
              >{{ offerTemplate.tagline.length }}/125</v-subheader>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!--Description-->
      <div>
        <v-row>
          <v-col cols="12" lg="6" class="mt-3">
            <p class="grey--text">A full description of your offer or event</p>
          </v-col>

          <v-col cols="12" lg="6">
            <textarea
              name="description"
              placeholder="Description"
              v-model="offerTemplate.description"
            />
            <v-row>
              <v-col
                :class="{
                  'valid-hint-md': this.$vuetify.breakpoint.md,
                  'valid-hint-xs': this.$vuetify.breakpoint.xs
                }"
              ></v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>

      <!--Terms and conditions-->
      <v-row>
        <v-col cols="12" lg="6" class="mt-3">
          <p class="grey--text">Any terms and conditions of the offer or event incl restrictions</p>
        </v-col>
        <v-col cols="12" lg="6">
          <textarea name="terms" placeholder="Terms and conditions" v-model="offerTemplate.terms" />
        </v-col>
      </v-row>

      <!--Add code-->
      <v-row>
        <v-col cols="12" lg="6" class="mt-3">
          <p class="grey--text">Add unique point of sale/barcode or QR code to redeem this offer</p>
        </v-col>
        <v-col cols="12" lg="6">
          <v-text-field
            type="text"
            name="code"
            outlined
            solo
            flat
            rounded
            placeholder="Add Point of Sale code"
            v-model="offerTemplate.code.value"
          ></v-text-field>
        </v-col>
      </v-row>

      <!--Set time and date-->
      <h1 class="my-3 mt-8" style="font-weight: 300;">Offer duration</h1>
      <v-divider />

      <v-row>
        <v-col cols="12">
          <date-config
            :rules="rules"
            :currentOffer="offer"
            @remove="removeDate"
            @reset="resetDatesArray"
            @generateDates="updateDatesArray"
            @generateDay="updateDatesArray"
          />
        </v-col>
      </v-row>

      <div class="mt-3" style="display: flex; justify-content: space-between;">
        <h1 style="font-weight: 300;">Pricing</h1>
        <v-btn text @click="addPricing">Add another</v-btn>
      </div>
      <v-divider />

      <v-alert
        v-if="offerTemplate.pricing.length > 1 && offerTemplate.pricing[0].tribe == null"
        class="info mt-6"
        dark
      >Note: Offer will be made public if no tribe is selected.</v-alert>

      <v-row v-for="(price, index) in offerTemplate.pricing" :key="index" class="pa-0">
        <v-col>
          <pricing :pricing="price" :tribes="allTribes" :rules="rules" />
        </v-col>
      </v-row>

      <h1 class="my-3 mt-8" style="font-weight: 300;">Display settings</h1>
      <v-divider class="mb-3" />

      <div class="pb-4" style="display: flex; justify-content: space-between;">
        <v-subheader>Display category</v-subheader>
        <v-subheader>
          Choose which category this event or offer should apply
          to
        </v-subheader>
      </div>

      <div v-if="offer != null" style="text-align: center;">
        <h1 style="font-weight: 300;">{{offerTemplate.category}}</h1>
      </div>

      <v-tabs
        v-model="tab"
        grow
        hide-slider
        centered
        background-color="transparent"
        class="my-5"
        dark
      >
        <v-chip color="accent">
          <v-tab
            v-for="(category, index) in categories"
            :key="index"
            :href="`#${index}`"
          >{{ category.tabName }}</v-tab>
        </v-chip>

        <v-tab-item v-for="(category, index) in categories" :key="index" :value="`${index}`">
          <tiles
            :category="category.apiName"
            :tiles="category.tiles"
            @add="pushTile"
            @remove="removeTile"
            :existingTiles="offerTemplate.tiles"
          ></tiles>
        </v-tab-item>
      </v-tabs>

      <h1 style="font-weight: 300;">Image</h1>
      <v-divider />

      <div style="margin: 20px auto; width: 100px;">
        <v-img v-if="!fetchedImg" src="@/assets/upload.png" @click="uploadImg()" />
        <div v-else style="display: flex; justify-content: center; margin-bottom: 20px;">
          <v-chip style="border: 1px solid #3F51B5; background-color: #f1f1f1;">
            <v-btn text right>
              <span style="color: #3F51B5;">Change</span>
            </v-btn>
          </v-chip>
        </div>
      </div>

      <input ref="input" type="file" accept="image/*" @change="stageImg" />

      <div
        v-if="(uploadedImg || fetchedImg) && this.offerTemplate.merchant"
        :class="{
          'review-sec-md': this.$vuetify.breakpoint.mdAndUp,
          'review-sec-xs': this.$vuetify.breakpoint.xs
        }"
      >
        <div style="width: 500px; height: 200px;">
          <v-card flat class="offer-card grey lighten-5">
            <v-img
              :src="!uploadedImg ? fetchedImg : uploadedImg"
              class="white--text"
              gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
            >
              <v-card-title class="card-title">
                <h4
                  v-if="offerTemplate.applies.length > 0"
                  style="font-weight: bold;"
                >{{ offerTemplate.title }}</h4>
              </v-card-title>
            </v-img>

            <div style="display: flex; height: 80px;">
              <span>
                <v-img
                  v-if="offerTemplate.merchant"
                  width="40"
                  class="mt-3 mr-4"
                  style="border-radius: 50%;"
                  :src="createMerchantUrl(offerTemplate.merchant)"
                />
              </span>
              <div style="display: flex; flex-direction: column; font-size: 20px;">
                <p v-if="offerTemplate.tagline">{{ offerTemplate.tagline }}</p>
                <span
                  v-if="offerTemplate.merchant"
                  class="grey--text"
                  style="font-size: 18px;"
                >{{ offerTemplate.merchant.name }}</span>
              </div>
            </div>
          </v-card>
        </div>
        <div
          :class="[
            this.$vuetify.breakpoint.md ? 'review-offer-md' : 'review-offer-lg'
          ]"
        >
          <offer v-if="!fetchedImg" :offer="offerTemplate" :image="image"></offer>
          <offer v-else :offer="offerTemplate" :image="fetchedImg"></offer>
        </div>
      </div>
    </div>
    <div style="display: flex; justify-content: center;">
      <v-chip style="border: 1px solid #3F51B5; background-color: #f1f1f1; margin: 30px 0 30px 0;">
        <v-btn text @click="save">
          <span style="color: #3F51B5;">Save</span>
        </v-btn>
      </v-chip>
    </div>
    <v-snackbar v-model="offerAdded" top :timeout="5000" color="primary">
      Offer added!
      <v-btn text @click="offerAdded = !offerAdded">
        <font-awesome-icon icon="times" />
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
// Components
import offer from "@/components/offer/offer";
import dateConfig from "@/components/dates/dateConfig";
import addOutlet from "@/components/outlets/addOutlet";
import addMerchant from "@/components/merchants/addMerchant";
import pricing from "@/components/pricing/pricing";
import tiles from "@/components/tiles/tiles";
import imageCropper from "@/components/cropper/imageCropper";

// Loading animations
import VueContentLoading from "vue-content-loading";

const uuid = require("uuid/v4");

// Vuex
import { mapActions, mapState } from "vuex";

// Rules
import rules from "@/rules.js";

// Utilities
import moment from "moment";
import { create } from "domain";
import { format } from "url";

export default {
  props: {
    offer: Object
  },

  components: {
    offer,
    dateConfig,
    addOutlet,
    addMerchant,
    pricing,
    tiles,
    imageCropper,
    VueContentLoading
  },

  data() {
    return {
      offerTemplate: {
        id: uuid(),
        merchant: {},
        owner: {},
        applies: [],
        pricing: [
          {
            tribe: null,
            price: "",
            previous: "",
            discount: ""
          }
        ],

        title: "",
        tagline: "",
        description: "",
        terms: "",
        code: {
          type: "link",
          value: ""
        },
        image: "",
        category: "",
        tiles: [],
        dates: []
      },

      uploadedImg: "",
      fetchedImg: "",

      rules,
      searchMerchant: "",
      outlets: [],
      merchantSelectMenu: false,
      addPricingBtn: true,
      disableDiscount: true,

      dialogs: {
        newOutlet: false,
        date: false,
        cropper: false
      },

      currentDates: [],

      offerAdded: false,
      invalidOffer: false,
      populated: false,
      loadingForm: false,
      imageData: "",

      tab: "-1",
      categories: [
        { tabName: "Eat", apiName: "eat", tiles: [] },
        { tabName: "Shop", apiName: "shop", tiles: [] },
        { tabName: "Do", apiName: "cool", tiles: [] }
      ],

      selectAllOutlets: false,

      aspectRatio: 750 / 340
    };
  },

  methods: {
    ...mapActions({
      // Merchant actions
      getMerchants: "merchants/fetchMerchants",
      findMerchant: "merchants/searchMerchants",
      toggleMerchantModal: "ui/merchantModalState",
      fetchMerchant: "merchants/fetchOne",
      recentMerchant: "merchants/fetchRecent",
      outletsForMerchant: "merchants/fetchOutletsForMerchant",

      // Outlet actions
      getOutlet: "outlets/fetchOutlet",
      getAllOutlets: "outlets/fetchAllOutlets",
      toggleOutletModal: "ui/outletModalState",

      // Offer actions
      createOffer: "offers/addOffer",
      updateOffer: "offers/updateOffer",
      fetchOne: "offers/fetchOne",

      // Tribe actions
      getTribes: "tribes/fetchTribes",

      // Tile actions
      getTiles: "tiles/fetchTiles",

      // Cropper actions
      toggleCropperModal: "ui/cropperModalState",
      sendImage: "cropper/imageToCrop",
      clearImg: "cropper/clear"
    }),

    createMerchantUrl(merchant) {
      return `${process.env.VUE_APP_IMAGE_URL}/merchant/${merchant.id}_${merchant.logo}`;
    },

    createImageUrl(offer) {
      return `${process.env.VUE_APP_IMAGE_URL}/offer/${offer.id}_${offer.image}`;
    },

    toggle() {
      this.$nextTick(() => {
        if (this.selectAllOutlets) {
          this.offerTemplate.applies = this.outlets;
        } else {
          this.offerTemplate.applies = [this.offerTemplate.merchant];
        }
      });
    },

    pushTile(val) {
      val.forEach(tile => {
        this.offerTemplate.tiles.push(tile);
      });
    },

    removeTile(val) {
      this.offerTemplate.tiles = val.splice(val.id, 1);
    },

    updateDatesArray(date) {
      this.offerTemplate.dates.push(date);
    },

    removeDate(rid) {
      let removedDates = this.offerTemplate.dates.filter(date => {
        return date.rid === rid;
      });
      this.offerTemplate.dates = removedDates;
    },

    resetDatesArray() {
      this.offerTemplate.dates = [];
    },

    save() {
      let offerTemplate = { ...this.offerTemplate };
      offerTemplate.owner = offerTemplate.merchant;
      if (!offerTemplate.code.value) {
        offerTemplate.code.value = " ";
      }

      if (
        !offerTemplate.merchant ||
        !offerTemplate.title ||
        !offerTemplate.category
      ) {
        this.invalidOffer = true;
      } else {
        this.invalidOffer = false;
      }

      if (
        offerTemplate.title.length > 75 ||
        offerTemplate.tagline.length > 125
      ) {
        this.invalidOffer = true;
      }

      if (this.invalidOffer === false) {
        if (this.offer != null) {
          offerTemplate._attachments = {
            [offerTemplate.image]: {
              data: this.uploadedImg.replace("data:image/jpeg;base64,", ""),
              content_type: "image/jpeg"
            }
          };

          this.updateOffer(offerTemplate).then(() => {
            this.$router.push("/allOffers");
          });
        } else {
          this.createOffer(offerTemplate).then(() => {
            this.$router.push("/allOffers");
            this.offerAdded = true;
          });
        }
      }
    },

    addPricing() {
      this.offerTemplate.pricing.push({
        tribe: null,
        price: "",
        previous: "",
        discount: ""
      });
    },

    async renderOffer(existingOffer) {
      try {
        if (existingOffer != null) {
          this.loadingForm = true;

          // Fetch merchant to edit
          existingOffer.merchant = await this.fetchMerchant(
            existingOffer.merchant.id
          );

          // Fetch outlets to edit
          if (existingOffer.applies[0].id == existingOffer.merchant.id) {
            offer.applies = [];
            let outlet = await this.outletsForMerchant(
              existingOffer.merchant.id
            );
            existingOffer.applies.push(outlet.items[0]);
          } else {
            for (let i = 0; i < existingOffer.applies.length; i++) {
              this.getOutlet(existingOffer.applies[i]).then(outlet => {
                existingOffer.applies[i] = outlet;
              });
            }
          }

          // Fetch tribe to edit
          let tribes = await this.allTribes;
          for (let i = 0; i < existingOffer.pricing.length; i++) {
            let tribe = tribes.find(tribe => {
              return tribe.id == existingOffer.pricing[i].id;
            });

            if (tribe) {
              existingOffer.pricing[i] = {
                tribe,
                discount: existingOffer.pricing[i].discount,
                previous: existingOffer.pricing[i].previous,
                price: existingOffer.pricing[i].price
              };
            }
          }

          if (existingOffer.pricing && existingOffer.pricing.length < 1) {
            existingOffer.pricing = [
              {
                tribe: null,
                price: "",
                previous: "",
                discount: ""
              }
            ];
          }

          // Fetch tiles to edit
          if (existingOffer.tiles[0] == undefined) {
            existingOffer.tiles.shift();
          }
          let category = existingOffer.category;
          let allTiles = await this.getTiles(category);
          let matchedTiles = [];

          for (let i = 0; i < existingOffer.tiles.length; i++) {
            let matchedTile = allTiles.find(tile => {
              return existingOffer.tiles[i].id == tile.id;
            });

            matchedTiles.push(matchedTile);
          }
          existingOffer.tiles = matchedTiles;

          // Fetch image to edit
          this.fetchedImg = await this.createImageUrl(existingOffer);

          // Fetch merchant logo
          this.createMerchantUrl(this.offer);
          this.offerTemplate = existingOffer;
          this.loadingForm = false;
        }
      } catch (err) {
        console.log(err);
      }
    },

    showOutletModal() {
      this.toggleOutletModal();
    },

    showMerchantModal() {
      this.toggleMerchantModal();
    },

    showCropperModal() {
      this.toggleCropperModal();
    },

    stageImg(data) {
      this.sendImage(data);
    },

    async uploadImg() {
      this.$refs.input.click();
      this.toggleCropperModal();
    },

    image() {
      return this.cropper.postCrop;
    }
  },

  computed: {
    ...mapState({
      offers: state => state.offers,
      merchants: state => state.merchants,
      tribes: state => state.tribes,
      locations: state => state.locations,
      cropper: state => state.cropper
    }),

    // MERCHANTS

    allMerchants() {
      return this.merchants.items;
    },

    merchantModal() {
      return this.ui.merchantModalState;
    },

    allTribes() {
      return this.tribes.items;
    },

    image() {
      return this.cropper.image;
    },

    loadingMerchant() {
      return this.merchants.loading;
    },

    error() {
      return this.merchants.error;
    },

    // OUTLETS

    canAddOutlet() {
      if (Object.entries(this.offerTemplate.merchant).length != 0) {
        return true;
      }
    },

    pricingFormat() {
      Object.values(this.offerTemplate.pricing).forEach(function(val) {
        if (typeof val === "string") {
          return false;
        }
      });

      return true;
    }
  },

  watch: {
    // Initial render of offer
    async offer(val) {
      this.renderOffer(val);
    },

    "merchants.items": function(newVal, oldVal) {
      if (newVal[0].id != oldVal[0].id) {
        this.offerTemplate.merchant.id = newVal[0].id;
        this.offerTemplate.merchant.name = newVal[0].name;
      }
    },

    "offerTemplate.merchant": function(val) {
      this.offerTemplate.merchant.id = val.id;
      this.offerTemplate.merchant.name = val.name;
    },

    searchMerchant(val) {
      if (val) {
        this.findMerchant(val);
      } else {
        this.getMerchants();
      }
    },

    allOutlets(newVal, oldVal) {
      let allOutlets = [...newVal];
      let newOutlet = allOutlets[0];
      this.outlets.push(newOutlet);
      this.offerTemplate.applies.push(newOutlet);
    },

    "offerTemplate.merchant": async function(val) {
      if (val.id) {
        let res = await this.outletsForMerchant(val.id);
        this.outlets = res.items;
      }
    },

    async tab(val) {
      const tiles = await this.getTiles(this.categories[parseInt(val)].apiName);
      this.categories[val].tiles = tiles;

      if (val == 0) {
        this.offerTemplate.category = "eat";
      } else if (val == 1) {
        this.offerTemplate.category = "shop";
      } else if (val == 2) {
        this.offerTemplate.category = "cool";
      }
    },

    selectAllOutlets(val) {
      if (val) {
        this.offerTemplate.applies = this.outlets;
      } else {
        this.offerTemplate.applies = [];
      }
    },

    "cropper.image": function(val) {
      this.uploadedImg = val;
    }
  },

  async mounted() {
    if (this.offer != null) {
      await this.renderOffer(this.offer);
    }
    this.getTribes();
  }
};
</script>

<style lang="sass" scoped>
@import @/styles/variables.sass
@import @/styles/mixins.sass
@import @/styles/base.sass

input
  border-radius: 30px

.add-offer
  margin-top: -60px
  background: $bg-color

  &__body
    padding: 10px
    transform: scale(.95)
    background: $c-white
    border-radius: 15px
    box-shadow: -2px -2px 3px rgba(0, 0, 0, .1)

    &__title
      font:
        size: 20px
        weight: 200
      margin-bottom: 30px

    &--autocomplete
      border-radius: 30px

    &--text-field
      outline: none
      height: 40px
      border-radius: 30px
      background: $c-white
      transition: border .3s

    &:focus
      border: 2px solid $dark-purple

  .shop-card
    display: inline-block
    margin-bottom: 5px
    margin-right: 20px

  .valid-offer
    border-left: 10px solid red
    background-color: rgb(58, 58, 58)

  .text-limit
    color: red

  .valid-hint-md
    position: relative
    top: -40px
    left: 1350px

  .valid-hint-xs
    position: relative
    top: -40px
    left: 390px

  .upload-lg
    position: relative
    left: 620px
    width: 80px

  .review-sec-lg
    display: flex
    justify-content: center

  .review-sec-md
    display: flex
    justify-content: center

  .review-sec-xs
    display: flex
    justify-content: center
    flex-direction: column
    padding: 10px

  .review-offer-md
    margin-left: 50px
    width: 300px

  .review-offer-lg
    margin-left: 100px
    width: 300px

</style>
